import React, { useMemo, useState } from 'react';
import { Target, Users, CircleDollarSign, Zap } from 'lucide-react';
import { useDashboardData } from '../components/DashboardShell';
import { useLocale } from '../context/LocaleContext';
import './Dashboard.css';

function formatStatusOption(status) {
  if (!status) return '';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Dashboard() {
  const { data, loading } = useDashboardData();
  const { t } = useLocale();
  const sessions = data?.sessions;

  const statusOptions = useMemo(() => {
    const list = sessions || [];
    const seen = new Set();
    for (const s of list) {
      const st = (s.status != null ? String(s.status) : '').trim();
      if (st) seen.add(st);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [sessions]);

  const [statusFilter, setStatusFilter] = useState('all');

  const filteredSessions = useMemo(() => {
    const list = sessions || [];
    if (statusFilter === 'all') return list;
    return list.filter((s) => (s.status != null ? String(s.status) : '') === statusFilter);
  }, [sessions, statusFilter]);

  if (!data && loading) return null;
  if (!data) return null;

  const { metrics, features } = data;
  const d = metrics?.deltas || {};

  const fmtPct = (n, signed = true) => {
    if (n == null || Number.isNaN(n)) return '';
    const v = Number(n);
    const s = signed && v > 0 ? '+' : '';
    return `${s}${v}%`;
  };

  const fmtSec = (n) => {
    if (n == null || Number.isNaN(n)) return '';
    const v = Number(n);
    const s = v <= 0 ? '' : '+';
    return `${s}${v.toFixed(2)}s`;
  };

  const pct = (feature) => Math.min(100, feature.usagePct ?? feature.usage_pct ?? 0);

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card stat-card--lift">
          <div className="stat-icon stat-icon--lucide" aria-hidden>
            <Target size={26} strokeWidth={1.75} />
          </div>
          <div className="stat-content">
            <p className="stat-label">{t('dashboard.activeDemos')}</p>
            <h3 className="stat-value">{metrics?.activeDemos ?? '—'}</h3>
            <p className="stat-change positive">
              {d.demosDeltaPct != null && Number(d.demosDeltaPct) > 0 ? '↑ ' : ''}
              {fmtPct(d.demosDeltaPct)} {t('dashboard.fromLastWeek')}
            </p>
          </div>
        </div>

        <div className="stat-card stat-card--lift">
          <div className="stat-icon stat-icon--lucide" aria-hidden>
            <Users size={26} strokeWidth={1.75} />
          </div>
          <div className="stat-content">
            <p className="stat-label">{t('dashboard.totalUsers')}</p>
            <h3 className="stat-value">
              {typeof metrics?.totalUsers === 'number' ? metrics.totalUsers.toLocaleString() : '—'}
            </h3>
            <p className="stat-change positive">
              {d.usersDeltaPct != null && Number(d.usersDeltaPct) > 0 ? '↑ ' : ''}
              {fmtPct(d.usersDeltaPct)} {t('dashboard.fromLastMonth')}
            </p>
          </div>
        </div>

        <div className="stat-card stat-card--lift">
          <div className="stat-icon stat-icon--lucide" aria-hidden>
            <CircleDollarSign size={26} strokeWidth={1.75} />
          </div>
          <div className="stat-content">
            <p className="stat-label">{t('dashboard.conversionRate')}</p>
            <h3 className="stat-value">{metrics?.conversionRate ?? '—'}%</h3>
            <p className="stat-change positive">
              {d.conversionDeltaPct != null && Number(d.conversionDeltaPct) > 0 ? '↑ ' : ''}
              {fmtPct(d.conversionDeltaPct)} {t('dashboard.fromLastWeek')}
            </p>
          </div>
        </div>

        <div className="stat-card stat-card--lift">
          <div className="stat-icon stat-icon--lucide" aria-hidden>
            <Zap size={26} strokeWidth={1.75} />
          </div>
          <div className="stat-content">
            <p className="stat-label">{t('dashboard.avgResponse')}</p>
            <h3 className="stat-value">
              {metrics?.responseTimeSec != null ? `${metrics.responseTimeSec}s` : '—'}
            </h3>
            <p className="stat-change positive">
              {fmtSec(d.responseDeltaSec)} {t('dashboard.faster')}
            </p>
          </div>
        </div>
      </div>

      <div className="section section--lift">
        <h2>{t('dashboard.platformFeatures')}</h2>
        <div className="features-grid">
          {(features || []).map((feature, index) => (
            <div key={feature.sort_order != null ? feature.sort_order : index} className="feature-card">
              <div className="feature-header">
                <span className="feature-icon">{feature.icon}</span>
                <h3>{feature.name}</h3>
              </div>
              <div className="feature-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${pct(feature)}%` }} />
                </div>
                <span className="progress-label">{t('dashboard.usage', { pct: pct(feature) })}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="section section--lift">
        <div className="section__header-row">
          <h2 className="section__title">{t('dashboard.recentSessions')}</h2>
          {statusOptions.length > 0 && (
            <select
              id="session-status-filter"
              className="sessions-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label={t('dashboard.filterByStatus')}
            >
              <option value="all">{t('dashboard.filterStatusAll')}</option>
              {statusOptions.map((st) => (
                <option key={st} value={st}>
                  {formatStatusOption(st)}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>{t('dashboard.colUser')}</th>
                <th>{t('dashboard.colProduct')}</th>
                <th>{t('dashboard.colDuration')}</th>
                <th>{t('dashboard.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="sessions-table__empty">
                    {(sessions || []).length === 0 ? t('dashboard.sessionsEmpty') : t('dashboard.sessionsNoMatch')}
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session) => (
                  <tr key={session.id}>
                    <td>{session.participantEmail}</td>
                    <td>{session.product}</td>
                    <td>{session.duration}</td>
                    <td>
                      <span className={`status-badge ${session.status}`}>{session.status}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
